import { useState, useEffect } from "react";
import PropTypes from "prop-types";
import ArgonBox from "components/ArgonBox";
import ArgonTypography from "components/ArgonTypography";
import ArgonAvatar from "components/ArgonAvatar";
import ArgonBadge from "components/ArgonBadge";
import VisibilityIcon from "@mui/icons-material/Visibility";
import DeleteIcon from "@mui/icons-material/Delete";
import { Button, Tooltip } from "@mui/material";
import { Link, useNavigate } from "react-router-dom";
import axios, { baseURL } from "utils/api";
import { toast } from "react-toastify";

// Profile cell
function Author({ image, name, id }) {
  const navigate = useNavigate();

  return (
    <ArgonBox display="flex" alignItems="center" px={0.5} py={0.1}>
      <ArgonBox mr={2}>
        <ArgonAvatar
          src={image}
          alt={name}
          size="sm"
          variant="rounded"
          sx={{
            "& img": {
              objectFit: "cover",
              width: "100%",
              height: "100%",
            },
          }}
        />
      </ArgonBox>
      <ArgonBox display="flex" flexDirection="column">
        <ArgonTypography
          textTransform="none"
          variant="button"
          fontWeight="medium"
          onClick={() => navigate(`/parents/profile/${id}`)}
          sx={{
            cursor: "pointer",
            textDecoration: "underline",
            "&:hover": {
              color: "#1976d2",
              textDecoration: "underline",
            },
          }}
        >
          {name}
        </ArgonTypography>
      </ArgonBox>
    </ArgonBox>
  );
}

Author.propTypes = {
  image: PropTypes.string,
  name: PropTypes.string.isRequired,
  id: PropTypes.string.isRequired,
};

// Function to get city and area from coordinates using reverse geocoding
const getAddressFromCoordinates = async (lat, lng) => {
  try {
    const response = await fetch(
      `https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lng}`
    );
    const data = await response.json();
    const city = data.address?.city || data.address?.town || data.address?.village || "-";
    const area = data.address?.suburb || data.address?.neighbourhood || "-";
    return { city, area };
  } catch (error) {
    console.error("Error fetching address:", error);
    return { city: "-", area: "-" };
  }
};
const StudentsList = ({ students = [] }) => {
  if (students.length === 0) {
    return (
      <ArgonTypography variant="caption" color="error">
        No Students
      </ArgonTypography>
    );
  }

  const displayStudent = students[0]; // first student
  const extraCount = students.length - 1; // extra students count

  const content = (
    <ArgonBox display="flex" alignItems="center" gap={1}>
      <ArgonBox display="flex" flexDirection="column" alignItems="flex-start">
        <ArgonTypography
          textTransform="none"
          sx={{
            backgroundColor: "#f0f0f0",
            padding: "4px 7px",
            borderRadius: "6px",
          }}
          variant="caption"
          fontWeight="medium"
          color="dark"
        >
          {displayStudent} {extraCount > 0 && `+${extraCount}`}
        </ArgonTypography>
      </ArgonBox>
    </ArgonBox>
  );

  const tooltipContent = (
    <ArgonBox sx={{ maxHeight: "200px", overflowY: "auto", padding: "4px" }}>
      <ArgonTypography
        variant="caption"
        fontWeight="bold"
        color="white"
        sx={{ display: "block", marginBottom: "8px" }}
      >
        All Students ({students.length}):
      </ArgonTypography>
      <ArgonBox>
        {students.map((student, index) => (
          <ArgonTypography
            key={index}
            variant="caption"
            color="white"
            sx={{ display: "block", marginBottom: "4px" }}
          >
            • {student}
          </ArgonTypography>
        ))}
      </ArgonBox>
    </ArgonBox>
  );

  return (
    <Tooltip
      title={tooltipContent}
      arrow
      placement="top"
      componentsProps={{
        tooltip: {
          sx: {
            backgroundColor: "rgba(0, 0, 0, 0.9)",
            backdropFilter: "blur(8px)",
            borderRadius: "8px",
            boxShadow: "0 8px 32px rgba(0, 0, 0, 0.3)",
            animation: "fadeInUp 0.3s ease-out",
            "@keyframes fadeInUp": {
              "0%": {
                opacity: 0,
                transform: "translateY(10px) scale(0.95)",
              },
              "100%": {
                opacity: 1,
                transform: "translateY(0) scale(1)",
              },
            },
          },
        },
        arrow: {
          sx: {
            color: "rgba(0, 0, 0, 0.9)",
          },
        },
      }}
    >
      <ArgonBox sx={{ cursor: "pointer" }}>{content}</ArgonBox>
    </Tooltip>
  );
};

StudentsList.propTypes = {
  students: PropTypes.arrayOf(PropTypes.string),
};

const useParentsData = (handleDeleteClick, refresh, activeTab = "All") => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [counts, setCounts] = useState({
    All: 0,
    ACTIVE: 0,
    HOLD: 0,
    COMPLETED: 0,
    DISCONTINUED: 0,
    SME: 0,
    COMBINED: 0,
    SEPARATE: 0,
  });
  const columns = [
    { name: "sno", align: "center" },
    { name: "profile", align: "left" },
    { name: "students", align: "left" },
    { name: "class Name", align: "left" },
    { name: "register Date", align: "left" },
    { name: "city", align: "left" },
    { name: "area", align: "left" },
    { name: "action", align: "center" },
  ];

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        // Fetch all parents first
        const parentsRes = await axios.get(`${baseURL}/parent`, {
          params: {
            limit: 1000, // Get all parents
          },
        });
        console.log("Parents API response:", parentsRes.data);

        if (!parentsRes.data.success) {
          toast.error("Failed to load parents");
          setRows([]);
          return;
        }

        const parents = parentsRes.data.data || [];

        if (parents.length === 0) {
          setRows([]);
          return;
        }

        // Fetch all students to get their info for each parent
        const studentsRes = await axios.get(`${baseURL}/student`, {
          params: {
            limit: 10000, // Get all students
          },
        });
        console.log("Students API response:", studentsRes.data);

        const students = studentsRes.data.success ? studentsRes.data.data || [] : [];

        // Fetch all class sessions to determine parent status
        const classSessionsRes = await axios.get(`${baseURL}/classes/sessions/all`, {
          params: {
            limit: 10000,
          },
        });
        console.log("Class Sessions API response:", classSessionsRes.data);

        const classSessions = classSessionsRes.data.success ? classSessionsRes.data.data || [] : [];

        // Create a map of parentId -> class session status
        const parentStatusMap = {};
        classSessions.forEach((session) => {
          const parentId = session?.parentId?._id || session?.parentId;
          if (parentId) {
            const parentIdStr = String(parentId);
            const sessionStatus = session.status;

            // Map session status to parent tab status
            let mappedStatus = "All";
            if (["ONGOING", "SCHEDULED", "CREATED"].includes(sessionStatus)) {
              mappedStatus = "ACTIVE";
            } else if (sessionStatus === "ON_HOLD") {
              mappedStatus = "HOLD";
            } else if (sessionStatus === "COMPLETED") {
              mappedStatus = "COMPLETED";
            }

            // Priority: ACTIVE > HOLD > COMPLETED (if parent has multiple sessions)
            if (!parentStatusMap[parentIdStr]) {
              parentStatusMap[parentIdStr] = mappedStatus;
            } else {
              // If already has a status, prioritize ACTIVE over others
              if (mappedStatus === "ACTIVE") {
                parentStatusMap[parentIdStr] = "ACTIVE";
              } else if (mappedStatus === "HOLD" && parentStatusMap[parentIdStr] !== "ACTIVE") {
                parentStatusMap[parentIdStr] = "HOLD";
              }
            }
          }
        });

        console.log("Parent Status Map:", parentStatusMap);

        // Create a map of parentId -> class type (Combined/SME/Separate)
        const parentClassTypeMap = {};
        classSessions.forEach((session) => {
          const parentId = session?.parentId?._id || session?.parentId;
          if (parentId) {
            const parentIdStr = String(parentId);
            const classType = session.classType;
            const studentCount = session.students?.length || 0;

            // Determine class type for parent
            if (classType === "SME") {
              parentClassTypeMap[parentIdStr] = "SME";
            } else if (classType === "COMBINED" || studentCount > 1) {
              if (parentClassTypeMap[parentIdStr] !== "SME") {
                parentClassTypeMap[parentIdStr] = "COMBINED";
              }
            } else if (studentCount === 1) {
              if (!parentClassTypeMap[parentIdStr]) {
                parentClassTypeMap[parentIdStr] = "SEPARATE";
              }
            }
          }
        });

        console.log("Parent Class Type Map:", parentClassTypeMap);

        // Create a map of parentId -> students info
        const parentStudentsMap = {};
        students.forEach((student) => {
          const parentId = student?.parentId?._id || student?.parentId;

          console.log(
            "Student:",
            student?.name,
            "ParentId:",
            parentId,
            "Parent Type:",
            typeof parentId
          );

          if (parentId) {
            // Convert to string to ensure matching
            const parentIdStr = String(parentId);

            if (!parentStudentsMap[parentIdStr]) {
              parentStudentsMap[parentIdStr] = {
                students: [],
                classes: [],
              };
            }
            parentStudentsMap[parentIdStr].students.push(student?.name || "-");
            if (student?.class?.name) {
              parentStudentsMap[parentIdStr].classes.push(student.class.name);
            }
          }
        });

        console.log("Parent Students Map:", parentStudentsMap);

        // Format rows for all parents with their status
        const allFormattedRows = parents.map((parent) => {
          const parentIdStr = String(parent._id);
          const studentInfo = parentStudentsMap[parentIdStr] || {
            students: [],
            classes: [],
          };

          // Determine parent status
          let parentStatus = "All";
          if (parent.isDeleted === true || parent.isDeleted === "true") {
            parentStatus = "DISCONTINUED";
          } else {
            parentStatus = parentStatusMap[parentIdStr] || "All";
          }

          // Determine parent class type
          const parentClassType = parentClassTypeMap[parentIdStr] || null;

          console.log(
            "Parent:",
            parent.fullName,
            "Status:",
            parentStatus,
            "ClassType:",
            parentClassType
          );

          return {
            parentId: parent._id,
            parentStatus: parentStatus,
            parentClassType: parentClassType,
            sno: (
              <ArgonTypography variant="caption" fontWeight="medium" color="text">
                -
              </ArgonTypography>
            ),

            profile: (
              <Author image={parent.profileImage} name={parent.fullName || "-"} id={parent._id} />
            ),

            students: <StudentsList students={studentInfo.students} />,

            "class Name": (
              <ArgonTypography
                variant="caption"
                color="text"
                fontWeight="medium"
                sx={{ padding: "4px" }}
              >
                {studentInfo.classes.length > 0 ? studentInfo.classes.join(", ") : "-"}
              </ArgonTypography>
            ),

            "register Date": (
              <ArgonTypography
                variant="caption"
                color="text"
                fontWeight="medium"
                sx={{ padding: "4px" }}
              >
                {parent.createdAt ? new Date(parent.createdAt).toLocaleDateString() : "-"}
              </ArgonTypography>
            ),

            city: (
              <ArgonTypography variant="caption" color="text" fontWeight="medium">
                {parent.city || "-"}
              </ArgonTypography>
            ),

            area: (
              <ArgonTypography variant="caption" color="text" fontWeight="medium">
                {parent.area || "-"}
              </ArgonTypography>
            ),

            status: (
              <ArgonBadge
                variant="gradient"
                badgeContent="Active"
                color="success"
                size="xs"
                container
              />
            ),

            action: (
              <ArgonBox display="flex" alignItems="center" gap={0}>
                {/* View */}
                <Button
                  variant="text"
                  size="large"
                  component={Link}
                  to={`/parents/profile/${parent._id}`}
                  sx={{ minWidth: "12px", p: 0 }}
                >
                  <VisibilityIcon color="primary" />
                </Button>

                {/* Delete */}
                <Button
                  variant="text"
                  size="large"
                  sx={{ minWidth: "32px", p: 0 }}
                  onClick={() => handleDeleteClick(parent._id)}
                >
                  <DeleteIcon color="error" />
                </Button>
              </ArgonBox>
            ),
          };
        });

        // Calculate counts for each status
        const statusCounts = {
          All: allFormattedRows.length,
          ACTIVE: allFormattedRows.filter((row) => row.parentStatus === "ACTIVE").length,
          HOLD: allFormattedRows.filter((row) => row.parentStatus === "HOLD").length,
          COMPLETED: allFormattedRows.filter((row) => row.parentStatus === "COMPLETED").length,
          DISCONTINUED: allFormattedRows.filter((row) => row.parentStatus === "DISCONTINUED")
            .length,
          SME: allFormattedRows.filter((row) => row.parentClassType === "SME").length,
          COMBINED: allFormattedRows.filter((row) => row.parentClassType === "COMBINED").length,
          SEPARATE: allFormattedRows.filter((row) => row.parentClassType === "SEPARATE").length,
        };

        console.log("Status Counts:", statusCounts);
        setCounts(statusCounts);

        // Filter rows based on active tab
        let filteredRows = allFormattedRows;
        if (activeTab === "SME") {
          filteredRows = allFormattedRows.filter((row) => row.parentClassType === "SME");
        } else if (activeTab === "COMBINED") {
          filteredRows = allFormattedRows.filter((row) => row.parentClassType === "COMBINED");
        } else if (activeTab === "SEPARATE") {
          filteredRows = allFormattedRows.filter((row) => row.parentClassType === "SEPARATE");
        } else if (activeTab !== "All") {
          filteredRows = allFormattedRows.filter((row) => row.parentStatus === activeTab);
        }

        // Re-index the filtered rows
        const finalRows = filteredRows.map((row, index) => ({
          ...row,
          sno: (
            <ArgonTypography variant="caption" fontWeight="medium" color="text">
              {index + 1}
            </ArgonTypography>
          ),
        }));

        setRows(finalRows);
        setLoading(false);
      } catch (err) {
        console.error("❌ Error fetching parents:", err);
        setLoading(false);
        toast.error(err.response?.data?.error || "Failed to load parents");
        setRows([]);
        setCounts({
          All: 0,
          ACTIVE: 0,
          HOLD: 0,
          COMPLETED: 0,
          DISCONTINUED: 0,
          SME: 0,
          COMBINED: 0,
          SEPARATE: 0,
        });
      }
    };

    fetchData();
  }, [refresh, activeTab]);

  return { columns, rows, counts, loading };
};

export default useParentsData;
